import { MenuProps } from 'antd';
import {
  DarwinDropdown,
  DarwinFlex,
  DarwinPopover,
  DarwinTag,
} from '@darwin-ui';
import styles from './UserConfiguration.module.css';
import { DMenuProps } from 'components/darwin-ui';
import { useUI } from 'contexts/UiContext';
import { UserByWallet, UserCompanyList } from 'types/userTypes';
import { useTranslation } from 'react-i18next';
import Icon from 'components/darwin-ui/Icon';
import { useWeb3 } from 'contexts/Web3Context';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { CopyOutlined, LinkOutlined } from '@ant-design/icons';
import { getExplorerLink } from 'utils/web3';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/routes';
import { useEffect, useState } from 'react';
import { useChangeUserLanguage } from 'api/services/userService';
import { LANGUAGE_OPTIONS } from 'constants/language';

type UserConfigurationProps = {
  companies: UserCompanyList;
  handleDisconnect: () => void;
  userData?: UserByWallet;
};

export function UserConfiguration({
  companies,
  handleDisconnect,
  userData,
}: UserConfigurationProps) {
  const { selectedCompany: selectedId, setSelectedCompany } = useUI();
  const { address } = useWeb3();
  const { openNotification } = useFloatNotification();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState('es');

  useEffect(() => {
    if (userData?.language) {
      i18n.changeLanguage(userData?.language);
      setCurrentLanguage(userData.language);
    }
  }, [userData, i18n]);

  const { mutateAsync: editLanguageUser } = useChangeUserLanguage();

  const selectedCompany = companies.find(item => item.id === selectedId)!;

  const profileDropdown: DMenuProps['items'] = companies.map(item => {
    const isCompanySelected = item.id === selectedCompany?.id;

    return {
      key: item.id,
      disabled: isCompanySelected,
      label: (
        <DarwinFlex className={styles['company-item']}>
          <span
            className={`${styles['company-name']} ${
              isCompanySelected ? styles['company-name-selected'] : ''
            }`}
          >
            {item.name}
          </span>
          {isCompanySelected && (
            <DarwinTag className={styles['selected-company-tag']}>
              {t('user-management.selected')}
            </DarwinTag>
          )}
        </DarwinFlex>
      ),
    };
  });

  const handleMenuClick: MenuProps['onClick'] = company => {
    setSelectedCompany(company.key);
    navigate(ROUTES.HOME);
  };

  const onCopyAddress = () => {
    openNotification(
      t('user-management.copy-address'),
      t('user-management.address-copied')
    );
    navigator.clipboard.writeText(address as string);
  };

  const languageItems: DMenuProps['items'] = LANGUAGE_OPTIONS.map(lang => ({
    key: lang.id,
    label: t(`language.${lang.key}`),
    disabled: currentLanguage === lang.id,
  }));

  const handleLanguageChange: MenuProps['onClick'] = async ({ key }) => {
    try {
      await editLanguageUser({
        languageData: { language: key as string },
      });
      i18n.changeLanguage(key);
      setCurrentLanguage(key);
      openNotification(
        t('success'),
        t('user-management.language-updated'),
        'success'
      );
    } catch {
      openNotification(
        'error',
        t('user-management.language-update-error'),
        'error'
      );
    }
  };

  return (
    <DarwinFlex className={styles['user-config-container']} align="center">
      {/* Language selector */}
      <DarwinDropdown
        menu={{
          items: languageItems,
          onClick: handleLanguageChange,
        }}
        placement="bottomRight"
        trigger={['click']}
      >
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a onClick={e => e.preventDefault()}>
          <DarwinFlex align="center" className={styles['language-trigger']}>
            <Icon name="language" size="xxl" />
          </DarwinFlex>
        </a>
      </DarwinDropdown>

      {/* Wallet info popover */}
      <DarwinPopover
        arrow
        align={{ offset: [0, -22] }}
        trigger={['click']}
        content={
          <DarwinFlex vertical className={styles['wallet-info']}>
            <DarwinFlex align="center" className={styles['wallet-flex']}>
              <Icon name="wallet" size="xxl" color="black" />
              <span className={styles['wallet-text']}>Mi Billetera</span>
            </DarwinFlex>
            <DarwinFlex align="center" className={styles['wallet-flex']}>
              <span className={styles['wallet-text']}>
                {t('user-management.address')}:{' '}
                <span className={styles['wallet-address-text']}>{address}</span>
              </span>

              <CopyOutlined
                onClick={onCopyAddress}
                className={styles['wallet-copy-icon']}
              />

              <a
                href={getExplorerLink(address as string)}
                target="_blank"
                rel="noreferrer"
              >
                <LinkOutlined />
              </a>
            </DarwinFlex>
          </DarwinFlex>
        }
      >
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a onClick={e => e.preventDefault()}>
          <DarwinFlex align="center" className={styles['wallet-trigger']}>
            <Icon name="wallet" size="xxl" />
          </DarwinFlex>
        </a>
      </DarwinPopover>

      {/* User/Primary Company Management */}
      <DarwinDropdown
        arrow
        align={{ offset: [10, 20] }}
        menu={{
          items: [
            {
              disabled: true,
              key: 'user-management',
              label: (
                <DarwinFlex
                  align="center"
                  className={styles['user-dropdown-header']}
                >
                  <Icon name="user-placeholder" color="black" size="xxl" />
                  <DarwinFlex vertical>
                    <span className={styles['selected-company-name']}>
                      {selectedCompany?.name}
                    </span>
                    <button
                      className={styles['disconnect-button']}
                      onClick={handleDisconnect}
                    >
                      {t('user-management.disconnect')}
                    </button>
                  </DarwinFlex>
                  <Icon name="chevron-down" />
                </DarwinFlex>
              ),
            },
            ...profileDropdown,
          ],
          onClick: handleMenuClick,
        }}
        placement="bottomRight"
        trigger={['click']}
      >
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a onClick={e => e.preventDefault()}>
          <DarwinFlex
            align="center"
            justify="start"
            className={styles['user-dropdown-trigger']}
          >
            <Icon name="user-placeholder" color="white" size="xxl" />
            <DarwinFlex vertical>
              <span className={styles['user-name']}>
                {userData?.firstName} {userData?.lastName}
              </span>
              <span className={styles['user-name']}>
                {selectedCompany?.name}
              </span>
            </DarwinFlex>
            <Icon name="chevron-down" size="lg" />
          </DarwinFlex>
        </a>
      </DarwinDropdown>
    </DarwinFlex>
  );
}
